import { CropFieldWrapper } from '../../models/models';

export interface GreenManureData {
  field: CropFieldWrapper;
  cropYear: number;
  customerId: number;
}

export interface GreenManureAnswers {
  nextMainCropId: number | null;
  isNonInversionTillage: boolean | null;
  nematodeConcerns: NematodeId[] | null;
  diseaseConcerns: DiseaseId[] | null;
  managementSystem: ManagementSystem | null;
  priorities: EcosystemPriority[] | null;
}

export interface CropFieldSnapshot {
  name: string;
  areaInHectares: number;
  mainCropId: number;
  agrifirmCropId: number;
  mainCropName: string;
  soilTypeId: number;
  soilTypeName: string;
  startDate: Date | string;
  endDate: Date | string;
  isSkal: boolean | null;
  isPlanetProof: boolean;
  province: string | null;
}

export interface GreenManureQuestionnaire {
  cropFieldId: number;
  cropYear: number;
  answers: GreenManureAnswers;
  cropFieldSnapshot: CropFieldSnapshot;
  isComplete: boolean;
  cropFieldSnapshotCapturedAtUtc: Date | string;
}

export interface GreenManureContext {
  customerId: number;
  cropFieldId: number;
  cropYear: number;
}

export interface ConcernOption<T extends string> {
  id: T;
  label: string;
  labelKey?: string;
}

export const NEMATODES = [
  {
    id: 'GloboderaPallidaRostochiensis',
    labelKey: 'greenManure.potatoCystNematode',
    label: 'Globodera pallida/rostochiensis',
  },
  { id: 'DitylenchusDipsaci', labelKey: 'greenManure.stemNematode', label: 'Ditylenchus dipsaci' },
  { id: 'DitylenchusDestructor', labelKey: 'greenManure.destructorNematode', label: 'Ditylenchus destructor' },
  { id: 'HeteroderaSchachtii', labelKey: 'greenManure.beetCystNematode', label: 'Heterodera schachtii' },
  { id: 'HeteroderaBetae', labelKey: 'greenManure.yellowBeetCystNematode', label: 'Heterodera betae' },
  { id: 'MeloidogyneHapla', labelKey: 'greenManure.northernRootKnotNematode', label: 'Meloidogyne hapla' },
  { id: 'MeloidogyneNaasi', labelKey: 'greenManure.grassRootKnotNematode', label: 'Meloidogyne naasi' },
  { id: 'MeloidogyneChitwoodi', labelKey: 'greenManure.maizeRootKnotNematode', label: 'Meloidogyne chitwoodi' },
  { id: 'MeloidogyneFallax', labelKey: 'greenManure.falseMaizeRootKnotNematode', label: 'Meloidogyne fallax' },
  { id: 'PratylenchusPenetrans', labelKey: 'greenManure.rootLesionNematode', label: 'Pratylenchus penetrans' },
  {
    id: 'ParatrichodorusPachydermus',
    labelKey: 'greenManure.freeLivingNematode',
    label: 'Paratrichodorus pachydermus',
  },
  { id: 'ParatrichodorusTeres', labelKey: 'greenManure.freeLivingNematode', label: 'Paratrichodorus teres' },
  { id: 'ParatrichodorusAnemones', labelKey: 'greenManure.freeLivingNematode', label: 'Paratrichodorus anemones' },
  { id: 'TrichodorusSimilis', labelKey: 'greenManure.freeLivingNematode', label: 'Trichodorus similis' },
  { id: 'TrichodorusPrimitivus', labelKey: 'greenManure.freeLivingNematode', label: 'Trichodorus primitivus' },
] as const;

export const DISEASES = [
  { id: 'PlasmodiophoraBrassicae', labelKey: 'greenManure.clubroot', label: 'Plasmodiophora brassicae' },
  { id: 'Rhizoctonia', label: 'Rhizoctonia' },
  { id: 'Verticillium', label: 'Verticillium' },
  { id: 'Fusarium', label: 'Fusarium' },
  { id: 'Sclerotinia', label: 'Sclerotinia' },
  { id: 'AphanomycesEuteiches', label: 'Aphanomyces euteiches' },
  { id: 'Colletotrichum', labelKey: 'greenManure.anthracnose', label: 'Colletotrichum' },
] as const;

export const MANAGEMENT_SYSTEMS = [
  { id: 'Winter', icon: 'grass', titleKey: 'winterTitle', descriptionKey: null },
  { id: 'Mulch', icon: 'agriculture', titleKey: 'mulchTitle', descriptionKey: 'mulchDescription' },
  { id: 'Permanent', icon: 'all_inclusive', titleKey: 'permanentTitle', descriptionKey: 'permanentDescription' },
] as const;
export type ManagementSystem = (typeof MANAGEMENT_SYSTEMS)[number]['id'];

export const ECOSYSTEM_PRIORITIES = [
  'OrganicMatter',
  'PestControl',
  'WeedSuppression',
  'NutrientLeaching',
  'Biodiversity',
  'NitrogenFixation',
] as const;
export type EcosystemPriority = (typeof ECOSYSTEM_PRIORITIES)[number];
export const PRIORITY_LABEL_KEYS: Record<EcosystemPriority, string> = {
  OrganicMatter: 'greenManure.organicMatter',
  PestControl: 'greenManure.pestControl',
  WeedSuppression: 'greenManure.weedSuppression',
  NutrientLeaching: 'greenManure.nutrientLeaching',
  Biodiversity: 'greenManure.biodiversity',
  NitrogenFixation: 'greenManure.nitrogenFixation',
};
export const ECOSYSTEM_PRIORITY_LIMIT = 3;

export type NematodeId = (typeof NEMATODES)[number]['id'];
export type DiseaseId = (typeof DISEASES)[number]['id'];
